import React from "react";
import { CheckCircle2 } from "lucide-react";
import Seo from "../components/site/Seo";
import { Container, SectionHeading, CtaBand, Eyebrow, PrimaryButton } from "../components/site/Ui";
import { IMG } from "../data/site";
import { useLang } from "../lib/LangContext";


const profileFields = [
  "Business", "Audience", "Tone of voice", "Services", "Products", "Location",
  "Promotions", "Social platforms", "Content preferences", "Brand colors", "Logo", "Photos",
];

const journey = [
  ["Sign Up", "Choose a package and create your account."],
  ["Build Your Brand Profile", "Answer questions about your business."],
  ["Upload Content", "Add photos, videos, logos and promotional information."],
  ["Monthly Content Creation", "PostMint prepares your content."],
  ["Review", "Review everything from your dashboard."],
  ["Request Changes", "Request changes before final delivery."],
  ["Download or Publish", "Download social media graphics, captions and your content calendar."],
];

export default function HowItWorks() {
  const { tr } = useLang();
  return (
    <>
      <Seo
        title="How it works — from business information to a full month of content"
        description="See how PostMint turns your business information, photos and promotions into a personalised monthly social media content plan for your Dutch business."
        path="/how-it-works"
      />

      <section className="mesh-warm" data-testid="how-hero">
        <Container className="py-16 lg:py-20">
          <Eyebrow>{tr("How it works")}</Eyebrow>
          <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
            {tr("From business information to a full month of content.")}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            {tr("Every PostMint customer receives a personalised Brand Profile. It is the foundation we use to keep every future post recognisably yours.")}
          </p>
        </Container>
      </section>

      <section className="border-y border-slate-200 bg-white py-20" data-testid="how-brand-profile">
        <Container className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              eyebrow="Brand Profile"
              title="One profile. Consistent content."
              body="Your Brand Profile stores everything we need so your content stays consistent month after month — no repeated briefings, no starting from scratch."
            />
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {profileFields.map((f) => (
                <li key={f} className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-[#F95738]" aria-hidden="true" />
                  {tr(f)}
                </li>
              ))}
            </ul>
          </div>
          <img
            src={IMG.phoneApps}
            alt="Phone showing social media apps used for planning content"
            loading="lazy"
            className="h-72 w-full rounded-3xl object-cover soft-shadow lg:h-96"
          />
        </Container>
      </section>

      <section className="py-20 sm:py-24" data-testid="how-journey">
        <Container>
          <SectionHeading eyebrow="Customer journey" title="What happens, step by step." />
          <ol className="mt-12 space-y-4">
            {journey.map(([title, body], i) => (
              <li
                key={title}
                className="flex gap-5 rounded-3xl border border-slate-200 bg-white p-6 soft-shadow sm:p-7"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F95738] font-display text-sm font-bold text-white">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-display text-lg font-semibold text-slate-900">{tr(title)}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{tr(body)}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-10">
            <PrimaryButton to="/get-started" testId="how-page-cta-button">
              {tr("Create My Content Plan")}
            </PrimaryButton>
          </div>
        </Container>
      </section>

      <CtaBand testId="how-final-cta-button" />
    </>
  );
}
